import type cytoscape from 'cytoscape';
import type { Theme } from '../../lib/theme';

/**
 * Stile der Zeichenfläche. Größen, Farben und Beschriftung kommen aus den Daten der Elemente (siehe StoryCanvas),
 * die Form aus der Klasse: circle (leer/weit weg), card (Box als SVG-Bild), event, period, axis, flag, handle, note (Notizzettel), grip;
 * background markiert Hintergrundereignisse (PF 15).
 */
export function buildStyle(t: Theme): cytoscape.StylesheetJson {
  return [
    {
      selector: 'node',
      style: {
        'background-color': 'data(color)',
        width: 'data(w)',
        height: 'data(h)',
        'border-width': 'data(bw)',
        'border-color': 'data(bc)',
        label: 'data(label)',
        color: t.text,
        'font-size': 'data(fs)',
        'font-family': '"Inter Variable", Inter, system-ui, sans-serif',
        'text-valign': 'bottom',
        'text-halign': 'center',
        'text-margin-y': 5,
        'text-wrap': 'wrap',
        'text-max-width': 'data(tw)',
        'text-outline-color': t.canvas,
        'text-outline-width': 2.5,
        'z-index': 'data(z)',
        'min-zoomed-font-size': 0,
        'overlay-opacity': 0,
      },
    },
    { selector: 'node.circle', style: { shape: 'ellipse' } },
    {
      selector: 'node.card',
      style: {
        shape: 'round-rectangle',
        'background-opacity': 0,
        'background-image': 'data(img)',
        'background-fit': 'cover',
        'background-clip': 'none',
        'bounds-expansion': 0,
      },
    },
    // Hintergrundereignis (PF 15) ohne Zeitlinie: gestrichelter Rand
    { selector: 'node.card.background', style: { 'border-width': 2, 'border-style': 'dashed', 'border-color': t.muted } },
    {
      // Inhalt eines Nodes (Punkte, Kacheln, Symbole) als Bild; ein Klick auf eine Kachel öffnet das Kind (siehe StoryCanvas)
      selector: 'node.content',
      style: { shape: 'rectangle', 'background-opacity': 0, 'background-image': 'data(img)', 'background-fit': 'cover', 'background-clip': 'none', 'border-width': 0, label: '' },
    },
    // Zeitpunkt: Symbol als Bild (zwei Dreiecke Spitze an Spitze mit Punkt), siehe eventSvg
    // Vorschaukarte (SVG)
    { selector: 'node.preview', style: { shape: 'round-rectangle', 'background-opacity': 0, 'background-image': 'data(img)', 'background-fit': 'contain', 'background-clip': 'none', 'border-width': 0, label: '' } },
    { selector: 'edge.previewline', style: { 'line-color': 'data(lc)', 'line-style': 'dashed', 'line-dash-pattern': [5, 5], width: 1.5, 'line-opacity': 0.75 } },
    { selector: 'node.event', style: { shape: 'rectangle', 'background-opacity': 0, 'background-image': 'data(img)', 'background-fit': 'contain', 'background-clip': 'none', 'border-width': 0, 'font-weight': 'bold' } },
    {
      // Zeitraum: Balken und Marke als Bild (siehe periodSvg)
      selector: 'node.period',
      style: {
        shape: 'round-rectangle',
        'background-opacity': 0,
        'background-image': 'data(img)',
        'background-fit': 'none',
        'background-width': '100%',
        'background-height': '100%',
        'background-clip': 'none',
        'border-width': 0,
      },
    },
    {
      // Name der Zeitlinie (bei mehreren): links von ihrem Start, auf halber Höhe der Startmarke (Abstand siehe relabel)
      selector: 'node.axis',
      style: { shape: 'rectangle', 'background-opacity': 0.35, 'border-width': 2, 'border-color': 'data(color)', 'text-halign': 'left', 'text-valign': 'center', 'text-margin-x': 'data(tmx)', 'text-margin-y': 'data(tmy)', 'font-weight': 'bold', 'text-wrap': 'none' },
    },
    { selector: 'node.flag', style: { shape: 'rectangle', 'text-valign': 'top', 'text-margin-y': -6, 'text-wrap': 'ellipsis' } },
    // Zeitfähnchen: Box mit der Zeit als Text, eine Linie führt zum Zeitstrahl
    { selector: 'node.flag-user, node.label-box', style: { color: 'data(tc)', shape: 'round-rectangle', 'background-opacity': 1, 'text-valign': 'center', 'text-halign': 'center', 'text-margin-y': 0, 'text-wrap': 'none', 'text-outline-width': 0, 'font-weight': 'bold' } },
    // Zeiten und Bild an der Beschriftungsbox von Ereignissen und Zeiträumen
    { selector: 'node.label-time', style: { shape: 'rectangle', 'background-opacity': 0, 'border-width': 0, 'text-valign': 'center', 'text-margin-y': 0, 'text-wrap': 'none', 'font-weight': 'bold' } },
    { selector: 'node.label-time-from', style: { 'text-halign': 'left' } },
    { selector: 'node.label-time-to', style: { 'text-halign': 'right' } },
    { selector: 'node.label-image', style: { shape: 'round-rectangle', 'background-opacity': 1, 'background-image': 'data(img)', 'background-fit': 'cover', 'background-clip': 'node', label: '' } },
    { selector: 'node.flagpin', style: { shape: 'ellipse', 'text-opacity': 0 } },
    // unscharfer Bereich einer ungenauen Zeit (AP-19): Bild in voller Breite, ohne Maus (Klicks gehen an Achse und Marken darunter)
    {
      selector: 'node.blur',
      style: { shape: 'round-rectangle', 'background-opacity': 0, 'background-image': 'data(img)', 'background-fit': 'none', 'background-width': '100%', 'background-height': '100%', 'background-clip': 'none', 'border-width': 0, label: '', events: 'no' },
    },
    // Stelle eines Notizzettels mit Zeit auf der Achse (PF 14): Marke bzw. schmaler Balken in seiner Farbe
    { selector: 'node.notepin', style: { shape: 'round-rectangle', 'text-opacity': 0, 'background-opacity': 0.9 } },
    { selector: 'edge.flagline', style: { width: 1.5, 'line-color': t.flagLine, 'curve-style': 'straight', label: '', 'z-index': 1 } },
    // Anfang und Ende des Zeitstrahls: die Zeit steht unter dem unteren Ende der Start- bzw. Endmarke (Abstand siehe relabel)
    { selector: 'node.flag-start, node.flag-end', style: { 'text-halign': 'center', 'text-valign': 'bottom', 'text-margin-x': 0, 'text-margin-y': 'data(tmy)', 'font-weight': 'bold', 'text-wrap': 'none' } },
    { selector: 'node.handle', style: { shape: 'round-rectangle', 'text-opacity': 0, 'background-opacity': 1 } },
    // Notizzettel: Papier mit Text als Bild in seiner Größe (siehe noteSvg); gewählt mit einem Griff unten rechts zum Ändern der Größe
    {
      selector: 'node.note',
      style: { shape: 'rectangle', 'background-opacity': 0, 'background-image': 'data(img)', 'background-fit': 'none', 'background-width': '100%', 'background-height': '100%', 'background-clip': 'none', 'border-width': 0, label: '' },
    },
    { selector: 'node.grip', style: { shape: 'round-rectangle', 'text-opacity': 0, 'background-opacity': 1 } },
    { selector: 'node.handle-picked', style: { 'background-opacity': 1 } },
    {
      // Gruppierungsrahmen der Bibliothek: durchscheinende Fläche mit gestricheltem Rand, Name oben im Rahmen
      selector: 'node.group',
      style: {
        shape: 'round-rectangle',
        'background-opacity': 0.12,
        'border-style': 'dashed',
        'text-valign': 'top',
        'text-halign': 'center',
        'text-margin-y': 22,
        'text-outline-width': 0,
        'font-weight': 'bold',
      },
    },
    { selector: 'node.resize', style: { shape: 'rectangle', 'text-opacity': 0 } },
    // Filter über den Graphen: Treffer leicht hervorgehoben, der Rest blass
    { selector: 'node.hit', style: { 'overlay-color': t.accent, 'overlay-opacity': 0.14, 'overlay-padding': 6 } },
    { selector: 'node.dim, edge.dim', style: { opacity: 0.16 } },
    { selector: 'node.sel', style: { 'overlay-color': t.accent, 'overlay-opacity': 0.22, 'overlay-padding': 8, 'z-index': 99 } },
    // der ausgewählte Zeitstrahl bleibt hinter seinen Ereignissen und Zeiträumen, eine ausgewählte Gruppe hinter ihren Einträgen
    { selector: 'node.axis.sel, node.flag.sel, node.flagpin.sel, node.group.sel', style: { 'z-index': 1 } },
    { selector: 'node.drop-ok', style: { 'overlay-color': t.ok, 'overlay-opacity': 0.34, 'overlay-padding': 12, 'z-index': 120 } },
    { selector: 'node.drop-no', style: { 'overlay-color': t.danger, 'overlay-opacity': 0.34, 'overlay-padding': 12, 'z-index': 120 } },
    {
      selector: 'edge',
      style: {
        width: 2,
        'line-color': t.border,
        'curve-style': 'bezier',
        'target-arrow-shape': 'none',
        label: 'data(label)',
        'font-size': 11,
        color: t.muted,
        'text-rotation': 'autorotate',
        'text-background-color': t.canvas,
        'text-background-opacity': 0.9,
        'text-background-padding': '2px',
        'overlay-opacity': 0,
        'z-index': 0,
      },
    },
    { selector: 'edge.sel', style: { 'line-color': t.accent, width: 3.5 } },
    // getragenes Ereignis bzw. getragener Zeitraum: das Original bleibt blass an seinem Platz, ein Platzhalter folgt der Maus
    { selector: 'node.carried', style: { opacity: 0.4 } },
  ] as unknown as cytoscape.StylesheetJson;
}
